import React, { useState } from 'react';
import { Link } from 'react-router';
import { Button, Input, Card, Alert, Loader } from '@rfacil/shared/ui';
import { useForm, useNotificacionStore } from '@rfacil/shared/hooks';
import { validationUtils } from '@rfacil/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faLock, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { useLogin } from '../hooks/useAuth';
import { useAuthStore } from '@rfacil/shared/services';

interface LoginFormProps {
  onLoginSuccess?: () => void;
}

/**
 * @component LoginForm
 * @description Un componente que proporciona una interfaz de usuario para iniciar sesión.
 * Incluye campos para nombre de usuario y contraseña, validación y manejo de errores.
 * Utiliza el gancho `useAuth` para manejar el proceso de inicio de sesión.
 * @param {object} props Las propiedades del componente.
 * @param {() => void} [props.onLoginSuccess] Una función de devolución de llamada que se llamará cuando el inicio de sesión sea exitoso.
 * @returns {React.ReactElement} El componente renderizado.
 */
export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const { showNotification } = useNotificacionStore();
  const [error, setError] = useState<string | null>(null);

  // Usar TanStack Query para el login
  const loginMutation = useLogin();
  const authStoreLoading = useAuthStore((state) => state.isLoading);
  const isLoading = loginMutation.isPending || authStoreLoading;

  const { values, errors, setValue, validateForm, isFormValid } = useForm(
    { user: '', password: '' },
    {
      user: (value: string) => (!value.trim() ? 'Usuario es requerido' : null),
      password: (value: string) =>
        !validationUtils.isValidPassword(value)
          ? 'Contraseña debe tener al menos 8 caracteres'
          : null,
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    try {
      await loginMutation.mutateAsync(values);
      onLoginSuccess?.();
    } catch (err) {
      console.error('Error al iniciar sesión:', err);
      const errorMessage = err instanceof Error ? err.message : 'Credenciales incorrectas';
      showNotification(errorMessage, 'error');
      setError(errorMessage);
    }
  };

  return (
    <>
      {isLoading && (
        <Loader
          message="Iniciando sesión..."
          subtitle="Validando tus credenciales"
        />
      )}
      <div className="min-h-screen flex flex-col bg-background-primary">
        <div className="flex-1 flex min-h-0">
        {/* Sección izquierda con imagen */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-gradient-to-br p-4">
          <div className="w-full ">
            <img
              src="/LOGOSRFACIL_CONSLOGAN.svg"
              alt="Rfacil Suite"
              className="w-full h-auto object-contain max-w-md mx-auto"
            />
          </div>
        </div>

        {/* Sección derecha con formulario */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-5">
          <Card className="w-full max-w-md" padding="lg">
            <div className="text-center mb-8">
              {/* Logo naranja con estrella */}
              <div className="flex justify-center mb-4">
                <div className="w-40 h-20 rounded-lg flex items-center justify-center ">

                  <img src="/ICONO IMAGOTIPO.png" alt="Logo" className="w-full h-full object-contain" />
                </div>
              </div>

              <h1 className="text-gray-800 mb-2 text-3xl font-bold">
                Rfacil Suite
              </h1>
              <p className="text-gray-600 text-sm">
                Sistema de Gestión Empresarial
              </p>

            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <Input
                  label="Usuario"
                  type="text"
                  value={values.user}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('user', e.target.value)
                  }
                  error={errors.user}
                  required
                  leftIcon={
                    <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                  }
                  placeholder="usuario@ejemplo.com"
                />
              </div>

              <div className="mb-6">
                <Input
                  label="Contraseña"
                  type="password"
                  value={values.password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('password', e.target.value)
                  }
                  error={errors.password}
                  required
                  leftIcon={
                    <FontAwesomeIcon icon={faLock} className="text-gray-400" />
                  }
                  placeholder="Ingresa tu contraseña"
                />
              </div>

              {error && (
                <Alert variant="error" className="mb-5">
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                loading={isLoading}
                fullWidth
                size="lg"
                disabled={!isFormValid()}
                className="bg-gradient-to-r from-host-500 to-host-600 hover:from-host-600 hover:to-host-700  text-text-secondary font-medium shadow-md"
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>

              <p className="text-center mt-4">
                <Link
                  to="/olvide-contrasena"
                  className="text-sm text-host-600 hover:underline font-medium"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </p>
            </form>
          </Card>
        </div>
        </div>

        <footer
          className="shrink-0 bg-gradient-to-b from-host-50/50 to-background-primary py-5 px-4 sm:px-8"
          role="contentinfo"
          aria-label="Aviso de privacidad"
        >
          <div className="max-w-2xl mx-auto flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
            <div
              className="flex justify-center sm:justify-start shrink-0"
              aria-hidden
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-host-100 text-host-600 shadow-sm ring-1 ring-host-200/50">
                <FontAwesomeIcon icon={faShieldHalved} className="text-lg" />
              </span>
            </div>
            <div className="text-center sm:text-left space-y-1.5">
              <p className="text-sm font-semibold text-gray-800">
                Privacidad y protección de datos
              </p>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                La información que nos proporciones se utiliza solo para gestión y comunicación
                relacionada con el servicio.{' '}

                <a
                  href="https://rfacil.com.mx/aviso-de-privacidad/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-host-600 font-medium underline decoration-host-300 underline-offset-2 hover:text-host-700 hover:decoration-host-500 transition-colors"
                >
                  Consulta nuestro Aviso de privacidad
                </a>
                .
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LoginForm;
